import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Image,
  Grid,
  GridItem,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  RadioGroup,
  Radio,
  Stack,
  Input,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon
} from '@chakra-ui/react'
import { useRecoilValue, useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import { purchaseBasicPass, getUser, getPointShopStock, giftPoint } from '../services/apiService'

// 샘플 포인트샵 아이템 데이터
const sampleItems = [
  {
    id: 1,
    name: 'BASIC 이용권 (1개월)',
    description: 'BASIC 등급 이용권을 1개월간 이용할 수 있습니다.',
    price: 300,
    image: 'https://img.freepik.com/premium-photo/service-excellence-high-quality-service-concept-continuous-improvement-invest-training_43780-10347.jpg?semt=ais_incoming&w=740&q=80',
    category: 'subscription',
    stock: 100
  }
]

const PointShop: React.FC = () => {
  const { t } = useTranslation()
  const [auth, setAuth] = useRecoilState(authState)
  const toast = useToast()
  const [items, setItems] = useState(sampleItems)
  const [loading, setLoading] = useState(false)
  const [dailyStock, setDailyStock] = useState(5) // 일일 재고 (5개)
  const [stockInfo, setStockInfo] = useState({
    dailyLimit: 5,
    todaySoldCount: 0,
    remainingStock: 5,
    isAvailable: true
  })
  
  // 구매 모달 관련 상태
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [purchaseType, setPurchaseType] = useState<'self' | 'other'>('self')
  const [targetUsername, setTargetUsername] = useState('')
  const [purchaseLoading, setPurchaseLoading] = useState(false)

  // 포인트 선물 모달 관련 상태
  const { isOpen: isGiftOpen, onOpen: onGiftOpen, onClose: onGiftClose } = useDisclosure()
  const [giftTargetUsername, setGiftTargetUsername] = useState('')
  const [giftPointAmount, setGiftPointAmount] = useState('')
  const [giftLoading, setGiftLoading] = useState(false)

  // 재고 정보 로드 함수
  const loadStockInfo = async () => {
    try {
      const response = await getPointShopStock()
      if (response.statusCode === 200 && response.data) {
        setStockInfo(response.data)
        setDailyStock(response.data.remainingStock)
      }
    } catch (error) {
      console.error('재고 정보 로드 실패:', error)
    }
  }

  // 컴포넌트 마운트 시 재고 정보 로드
  useEffect(() => {
    loadStockInfo()
  }, [])

  const handlePurchaseClick = (item: any) => {
    if (!auth.id) {
      toast({
        title: '로그인이 필요합니다',
        description: '포인트샵을 이용하려면 로그인해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    setSelectedItem(item)
    setPurchaseType('self')
    setTargetUsername('')
    onOpen()
  }

  const handleConfirmPurchase = async () => {
    if (!selectedItem) return

    setPurchaseLoading(true)
    
    try {
      const response = await purchaseBasicPass({
        purchaseType,
        targetUsername: purchaseType === 'other' ? targetUsername : undefined
      })

      // 백엔드가 실패 케이스도 200으로 반환하므로 message와 data를 확인
      if (response && response.statusCode === 200 && response.data) {
        const message = purchaseType === 'self' 
          ? `${selectedItem.name}을(를) 구매했습니다.`
          : `${targetUsername}님에게 ${selectedItem.name}을(를) 선물했습니다.`
        
        // 구매 완료 후 사용자 정보를 다시 가져와서 포인트 잔액 업데이트
        try {
          const { data: userResponse } = await getUser()
          if (userResponse.statusCode === 200 && userResponse.data) {
            setAuth(prevAuth => ({
              ...prevAuth,
              pointBalance: userResponse.data.pointBalance
            }))
          }
        } catch (userError) {
          console.error('사용자 정보 업데이트 실패:', userError)
          // 사용자 정보 업데이트 실패해도 구매는 성공했으므로 계속 진행
        }

        // 재고 정보 즉시 갱신
        await loadStockInfo()
        
        toast({
          title: '구매 완료',
          description: message,
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        
        onClose()
        
        // 모달 닫힌 후 재고 정보 한 번 더 갱신 (확실하게)
        setTimeout(() => {
          loadStockInfo()
        }, 500)
      } else {
        // 실패 케이스 (data가 null이거나 없음)
        throw new Error(response?.message || '구매에 실패했습니다.')
      }
    } catch (error: any) {
      let errorMessage = '구매 중 오류가 발생했습니다.'
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast({
        title: '구매 실패',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setPurchaseLoading(false)
    }
  }

  const handleGiftPoint = async () => {
    if (!giftTargetUsername.trim() || !giftPointAmount.trim()) {
      toast({
        title: '입력 오류',
        description: '받는 사람 아이디와 포인트 금액을 모두 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    const pointAmount = parseFloat(giftPointAmount)
    if (isNaN(pointAmount) || pointAmount <= 0) {
      toast({
        title: '입력 오류',
        description: '올바른 포인트 금액을 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (pointAmount > parseFloat(auth.pointBalance || '0')) {
      toast({
        title: '포인트 부족',
        description: '보유 포인트가 부족합니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }

    setGiftLoading(true)
    
    try {
      const response = await giftPoint({
        targetUsername: giftTargetUsername.trim(),
        pointAmount: pointAmount
      })

      if (response.data.statusCode === 200 && response.data.data) {
        // 포인트 선물 완료 후 사용자 정보를 다시 가져와서 포인트 잔액 업데이트
        try {
          const { data: userResponse } = await getUser()
          if (userResponse.statusCode === 200 && userResponse.data) {
            setAuth(prevAuth => ({
              ...prevAuth,
              pointBalance: userResponse.data.pointBalance
            }))
          }
        } catch (userError) {
          console.error('사용자 정보 업데이트 실패:', userError)
        }

        toast({
          title: '포인트 선물 완료',
          description: `${giftTargetUsername}님에게 포인트 ${pointAmount}P를 성공적으로 선물했습니다.`,
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        
        onGiftClose()
        setGiftTargetUsername('')
        setGiftPointAmount('')
      } else {
        throw new Error(response.data.message || '포인트 선물에 실패했습니다.')
      }
    } catch (error: any) {
      let errorMessage = '포인트 선물 중 오류가 발생했습니다.'
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast({
        title: '포인트 선물 실패',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setGiftLoading(false)
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'subscription': return 'cyan'
      case 'points': return 'green'
      case 'service': return 'blue'
      default: return 'gray'
    }
  }

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'subscription': return '이용권'
      case 'service': return '서비스'
      default: return category
    }
  }

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* 헤더 */}
          <Box textAlign="center">
            <Heading size="xl" mb={4} color="white">
              🎁 포인트샵
            </Heading>
            <Text fontSize="lg" color="gray.400" mb={6}>
              포인트로 다양한 혜택을 구매하세요
            </Text>
            {auth.id && (
              <Box 
                bgGradient="linear(to-r, brand.500, purple.600)" 
                borderRadius="2xl" 
                p={8} 
                color="white"
                boxShadow="0 0 20px rgba(0, 200, 255, 0.3)"
                mb={8}
                position="relative"
                overflow="hidden"
              >
                <Box position="absolute" top={0} left={0} w="full" h="full" bg="whiteAlpha.100" backdropFilter="blur(5px)" zIndex={0} />
                <VStack spacing={4} position="relative" zIndex={1}>
                  <VStack spacing={1}>
                    <Text fontSize="sm" opacity={0.9} fontWeight="medium">현재 보유 포인트</Text>
                    <Text fontSize="4xl" fontWeight="bold" letterSpacing="tight">
                      {Math.floor(parseFloat(auth.pointBalance || '0')).toLocaleString()} P
                    </Text>
                    <Badge colorScheme="whiteAlpha" variant="outline" px={2} py={0.5} borderRadius="full">
                      1P = 1USDT 동등 가치
                    </Badge>
                  </VStack>
                  
                  <Button
                    colorScheme="whiteAlpha"
                    variant="solid"
                    size="md"
                    onClick={onGiftOpen}
                    bg="whiteAlpha.200"
                    _hover={{ bg: 'whiteAlpha.300', transform: 'translateY(-2px)' }}
                    color="white"
                    border="1px solid rgba(255, 255, 255, 0.3)"
                    leftIcon={<span style={{ fontSize: '1.2em' }}>🎁</span>}
                  >
                    포인트 선물하기
                  </Button>
                </VStack>
              </Box>
            )}
          </Box>


          {/* 상품 목록 */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
            {items.map(item => (
              <GridItem key={item.id}>
                <Card
                  maxW="sm"
                  mx="auto"
                  bg="dark.card"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  borderRadius="xl"
                  overflow="hidden"
                  _hover={{ 
                    transform: 'translateY(-5px)', 
                    boxShadow: '0 0 20px rgba(0, 200, 255, 0.2)',
                    borderColor: 'brand.500'
                  }}
                  transition="all 0.3s ease"
                >
                  <CardHeader pb={2}>
                    <HStack justify="space-between" align="start">
                      <Badge 
                        colorScheme={getCategoryColor(item.category)} 
                        variant="solid"
                        px={2}
                        py={1}
                        borderRadius="md"
                      >
                        {getCategoryLabel(item.category)}
                      </Badge>
                      <Text fontSize="xs" color="gray.400">
                        남은 재고: <span style={{ color: '#fff' }}>{stockInfo.remainingStock}</span> / {stockInfo.dailyLimit}
                      </Text>
                    </HStack>
                  </CardHeader>
                  
                  <CardBody pt={0}>
                    <VStack spacing={4} align="stretch">
                      <Box borderRadius="lg" overflow="hidden" height="150px">
                        <Image
                          src={item.image}
                          alt={item.name}
                          width="100%"
                          height="100%"
                          objectFit="cover"
                          transition="transform 0.3s"
                          _hover={{ transform: 'scale(1.05)' }}
                        />
                      </Box>
                      
                      <VStack spacing={2} align="stretch">
                        <Heading size="md" color="white">
                          {item.name}
                        </Heading>
                        <Text fontSize="sm" color="gray.400" noOfLines={2} minH="40px">
                          {item.description}
                        </Text>
                      </VStack>

                      <VStack spacing={3} align="stretch">
                        <HStack justify="space-between" align="center">
                          <VStack spacing={0} align="start">
                            <Text fontSize="xl" fontWeight="bold" color="cyan.400">
                              {item.price.toLocaleString()} P
                            </Text>
                          </VStack>
                          
                          <Button
                            colorScheme="brand"
                            size="sm"
                            onClick={() => handlePurchaseClick(item)}
                            isDisabled={!auth.id || !stockInfo.isAvailable}
                            bgGradient="linear(to-r, brand.500, cyan.500)"
                            _hover={{
                              bgGradient: "linear(to-r, brand.400, cyan.400)",
                              boxShadow: "0 0 10px rgba(0, 200, 255, 0.4)"
                            }}
                          >
                            {!stockInfo.isAvailable ? '일일 한도 초과' : '구매하기'}
                          </Button>
                        </HStack>
                        
                        {auth.id && (
                          <Box 
                            bg={parseFloat(auth.pointBalance || '0') >= item.price ? 'rgba(72, 187, 120, 0.1)' : 'rgba(245, 101, 101, 0.1)'}
                            p={3} 
                            borderRadius="lg" 
                            border="1px solid"
                            borderColor={parseFloat(auth.pointBalance || '0') >= item.price ? 'green.500' : 'red.500'}
                          >
                            <HStack justify="space-between" fontSize="sm">
                              <Text color="gray.400">보유 포인트:</Text>
                              <Text 
                                fontWeight="bold" 
                                color={parseFloat(auth.pointBalance || '0') >= item.price ? 'green.400' : 'red.400'}
                              >
                                {Math.floor(parseFloat(auth.pointBalance || '0')).toLocaleString()} P
                              </Text>
                            </HStack>
                            {parseFloat(auth.pointBalance || '0') < item.price && (
                              <Text fontSize="xs" color="red.400" mt={1}>
                                부족: {(item.price - Math.floor(parseFloat(auth.pointBalance || '0'))).toLocaleString()} P
                              </Text>
                            )}
                          </Box>
                        )}
                      </VStack>
                    </VStack>
                  </CardBody>
                </Card>
              </GridItem>
            ))}
          </Grid>

        </VStack>
      </Container>

      {/* 구매 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent bg="dark.card" border="1px solid" borderColor="whiteAlpha.200">
          <ModalHeader color="white">이용권 구매</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            {selectedItem && (
              <VStack spacing={4} align="stretch">
                <Box p={4} bg="whiteAlpha.50" borderRadius="xl" border="1px solid" borderColor="whiteAlpha.100">
                  <HStack spacing={4}>
                    <Image
                      src={selectedItem.image}
                      alt={selectedItem.name}
                      boxSize="60px"
                      objectFit="cover"
                      borderRadius="md"
                    />
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold" color="white">{selectedItem.name}</Text>
                      <Text fontSize="sm" color="gray.400">{selectedItem.description}</Text>
                      <Text fontSize="lg" fontWeight="bold" color="cyan.400">
                        {selectedItem.price.toLocaleString()} P
                      </Text>
                    </VStack>
                  </HStack>
                </Box>

                <FormControl>
                  <FormLabel color="gray.300">구매 옵션</FormLabel>
                  <RadioGroup value={purchaseType} onChange={(value) => setPurchaseType(value as 'self' | 'other')}>
                    <Stack direction="column" spacing={2}>
                      <Radio value="self" colorScheme="cyan"><Text color="gray.300">본인에게 적용</Text></Radio>
                      <Radio value="other" colorScheme="cyan"><Text color="gray.300">타인에게 선물</Text></Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                {purchaseType === 'other' && (
                  <FormControl>
                    <FormLabel color="gray.300">받는 사람 아이디</FormLabel>
                    <Input
                      placeholder="아이디를 입력하세요"
                      value={targetUsername}
                      onChange={(e) => setTargetUsername(e.target.value)}
                      bg="dark.bg"
                      borderColor="whiteAlpha.200"
                      color="white"
                      _placeholder={{ color: 'gray.500' }}
                    />
                  </FormControl>
                )}

                <Box 
                  bg="rgba(66, 153, 225, 0.1)" 
                  p={4} 
                  borderRadius="xl" 
                  border="1px solid" 
                  borderColor="blue.500"
                >
                  <VStack spacing={2} align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.400">현재 보유 포인트:</Text>
                      <Text fontSize="sm" fontWeight="bold" color="blue.300">
                        {Math.floor(parseFloat(auth.pointBalance || '0')).toLocaleString()} P
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.400">구매 비용:</Text>
                      <Text fontSize="sm" fontWeight="bold" color="red.300">
                        -{selectedItem.price.toLocaleString()} P
                      </Text>
                    </HStack>
                    <HStack justify="space-between" borderTop="1px solid" borderColor="whiteAlpha.200" pt={2}>
                      <Text fontSize="sm" fontWeight="bold" color="gray.300">구매 후 잔액:</Text>
                      <Text fontSize="sm" fontWeight="bold" color={parseFloat(auth.pointBalance || '0') >= selectedItem.price ? 'green.400' : 'red.400'}>
                        {(Math.floor(parseFloat(auth.pointBalance || '0')) - selectedItem.price).toLocaleString()} P
                      </Text>
                    </HStack>
                  </VStack>
                </Box>

                <Alert status="info" bg="whiteAlpha.100" border="1px solid" borderColor="blue.500" borderRadius="md">
                  <AlertIcon color="blue.400" />
                  <Box>
                    <Text fontSize="sm" color="white">
                      {purchaseType === 'self' 
                        ? '본인의 BASIC 이용권 기간이 1개월 연장됩니다.'
                        : `${targetUsername}님의 BASIC 이용권 기간이 1개월 연장됩니다.`
                      }
                    </Text>
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      남은 재고: {stockInfo.remainingStock}개 / {stockInfo.dailyLimit}개 (오늘 판매: {stockInfo.todaySoldCount}개)
                    </Text>
                  </Box>
                </Alert>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} color="gray.400" _hover={{ bg: 'whiteAlpha.100', color: 'white' }}>
              취소
            </Button>
            <Button
              colorScheme="cyan"
              onClick={handleConfirmPurchase}
              isLoading={purchaseLoading}
              loadingText="구매중..."
              isDisabled={
                (purchaseType === 'other' && !targetUsername.trim()) ||
                (selectedItem && parseFloat(auth.pointBalance || '0') < selectedItem.price) ||
                !stockInfo.isAvailable
              }
              bgGradient="linear(to-r, cyan.500, blue.500)"
              _hover={{
                bgGradient: "linear(to-r, cyan.400, blue.400)",
                boxShadow: "0 0 15px rgba(0, 200, 255, 0.5)"
              }}
            >
              {selectedItem && parseFloat(auth.pointBalance || '0') < selectedItem.price 
                ? '포인트 부족' 
                : !stockInfo.isAvailable 
                ? '일일 한도 초과'
                : '구매하기'
              }
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 포인트 선물 모달 */}
      <Modal isOpen={isGiftOpen} onClose={onGiftClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent bg="dark.card" border="1px solid" borderColor="whiteAlpha.200">
          <ModalHeader color="white">포인트 선물하기</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box p={4} bg="rgba(236, 72, 153, 0.1)" borderRadius="xl" border="1px solid" borderColor="pink.500">
                <VStack spacing={2} align="stretch">
                  <Text fontSize="sm" fontWeight="bold" color="pink.400">
                    💝 포인트 선물 안내
                  </Text>
                  <Text fontSize="sm" color="pink.300">
                    • 보유 포인트를 다른 사용자에게 선물할 수 있습니다
                  </Text>
                  <Text fontSize="sm" color="pink.300">
                    • 선물한 포인트는 즉시 차감되며 되돌릴 수 없습니다
                  </Text>
                  <Text fontSize="sm" color="pink.300">
                    • 선물 내역은 히스토리에 기록됩니다
                  </Text>
                </VStack>
              </Box>

              <FormControl>
                <FormLabel color="gray.300">받는 사람 아이디</FormLabel>
                <Input
                  placeholder="아이디를 입력하세요"
                  value={giftTargetUsername}
                  onChange={(e) => setGiftTargetUsername(e.target.value)}
                  bg="dark.bg"
                  borderColor="whiteAlpha.200"
                  color="white"
                  _placeholder={{ color: 'gray.500' }}
                />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300">선물할 포인트 금액</FormLabel>
                <Input
                  type="number"
                  placeholder="포인트 금액을 입력하세요"
                  value={giftPointAmount}
                  onChange={(e) => setGiftPointAmount(e.target.value)}
                  min="1"
                  max={Math.floor(parseFloat(auth.pointBalance || '0'))}
                  bg="dark.bg"
                  borderColor="whiteAlpha.200"
                  color="white"
                  _placeholder={{ color: 'gray.500' }}
                />
              </FormControl>

              <Box 
                bg="rgba(66, 153, 225, 0.1)" 
                p={4} 
                borderRadius="xl" 
                border="1px solid" 
                borderColor="blue.500"
              >
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.400">현재 보유 포인트:</Text>
                    <Text fontSize="sm" fontWeight="bold" color="blue.300">
                      {Math.floor(parseFloat(auth.pointBalance || '0')).toLocaleString()} P
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.400">선물할 포인트:</Text>
                    <Text fontSize="sm" fontWeight="bold" color="red.300">
                      -{giftPointAmount ? parseInt(giftPointAmount).toLocaleString() : '0'} P
                    </Text>
                  </HStack>
                  <HStack justify="space-between" borderTop="1px solid" borderColor="whiteAlpha.200" pt={2}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.300">선물 후 잔액:</Text>
                    <Text fontSize="sm" fontWeight="bold" color={parseFloat(auth.pointBalance || '0') >= parseFloat(giftPointAmount || '0') ? 'green.400' : 'red.400'}>
                      {(Math.floor(parseFloat(auth.pointBalance || '0')) - (parseInt(giftPointAmount) || 0)).toLocaleString()} P
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              <Alert status="warning" bg="rgba(237, 137, 54, 0.1)" border="1px solid" borderColor="orange.500" borderRadius="md">
                <AlertIcon color="orange.400" />
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="orange.300">
                    주의사항
                  </Text>
                  <Text fontSize="xs" color="orange.200" mt={1}>
                    포인트 선물은 되돌릴 수 없습니다. 받는 사람의 아이디를 정확히 확인해주세요.
                  </Text>
                </Box>
              </Alert>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onGiftClose} color="gray.400" _hover={{ bg: 'whiteAlpha.100', color: 'white' }}>
              취소
            </Button>
            <Button
              colorScheme="pink"
              onClick={handleGiftPoint}
              isLoading={giftLoading}
              loadingText="선물중..."
              isDisabled={
                !giftTargetUsername.trim() ||
                !giftPointAmount.trim() ||
                parseFloat(giftPointAmount || '0') <= 0 ||
                parseFloat(auth.pointBalance || '0') < parseFloat(giftPointAmount || '0')
              }
              bgGradient="linear(to-r, pink.500, purple.500)"
              _hover={{
                bgGradient: "linear(to-r, pink.400, purple.400)",
                boxShadow: "0 0 15px rgba(236, 72, 153, 0.5)"
              }}
            >
              포인트 선물하기
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DefaultLayout>
  )
}

export default PointShop
